import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ROUTES } from "@/routes/path"
import { useMutation } from "@tanstack/react-query"
import { register as registerUser } from "@/lib/http/auth"
import { ErrorCard } from "@/components/ui/error"
import { useAuth, useSetEmail } from "@/hooks/useAuth"
import { useGoogleAuth } from "@/hooks/useGoogleAuth"
import { useGitHubAuth } from "@/hooks/useGitHubAuth"

const registerSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  fullName: z.string().min(2, "Full name is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
})

type RegisterFormValues = z.infer<typeof registerSchema>

export default function Register() {
  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { email } = useAuth()
  const setEmail = useSetEmail()
  const { handleGoogleAuth, isLoading: googleLoading } = useGoogleAuth()
  const { handleGitHubAuth, isLoading: githubLoading } = useGitHubAuth()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: email || "",
    },
  })

  const emailValue = watch("email")

  useEffect(() => {
    setEmail(emailValue)
  }, [emailValue, setEmail])

  const mutation = useMutation({
    mutationFn: async (data: RegisterFormValues) => {
      setIsLoading(true)
      setFormError(null)
      return await registerUser(data.email, data.password, data.fullName)
    },
    onSuccess: () => {
      navigate(ROUTES.AUTH.CONFIRM)
    },
    onError: (error: any) => {
      setFormError(error?.response?.data?.error || "An unknown error occurred.")
    },
    onSettled: () => {
      setIsLoading(false)
    },
  })

  async function onSubmit(data: RegisterFormValues) {
    mutation.mutate(data)
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-zinc-900 lg:flex-row">
      {/* Left column: features */}
      <div className="z-0 hidden w-1/2 flex-col bg-gradient-to-tr from-[#f1977e] via-[#dcfa91] to-[#98c22b] px-16 lg:flex">
        <div className="mt-10">
          <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
        </div>
        <ul className="mt-24 space-y-8 text-black">
          <li>
            <h5 className="border-l-2 border-black pl-4 text-lg font-semibold">Get started quickly</h5>
            <p className="mt-1 pl-4">Create an account in minutes and start talking to your data.</p>
          </li>
          <li>
            <h5 className="border-l-2 border-black pl-4 text-lg font-semibold">Support any business model</h5>
            <p className="mt-1 pl-4">
              Ecommerce, fintech, SaaS platforms, marketplaces, and more—all within a unified platform.
            </p>
          </li>
          <li>
            <h5 className="border-l-2 border-black pl-4 text-lg font-semibold">Join millions of businesses</h5>
            <p className="mt-1 pl-4">Ledgerbeam is trusted by ambitious startups and enterprises of every size.</p>
          </li>
        </ul>
      </div>
      <div className="mt-6 flex justify-center lg:hidden">
        <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
      </div>
      {/* Right column: registration card */}
      <div className="relative flex flex-1 justify-center p-4 md:items-center lg:py-10">
        <div className="relative z-10 w-full max-w-lg">
          <div className="rounded-xl bg-white p-8 shadow-xl dark:bg-zinc-800">
            <h1 className="mb-6 text-2xl font-semibold dark:text-zinc-100">Create your Ledgerbeam account</h1>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-6 space-y-4">
                <div>
                  <Input
                    id="email"
                    label="Email"
                    placeholder="name@company.com"
                    type="email"
                    disabled={isLoading}
                    {...register("email")}
                    error={errors.email?.message}
                    touched={touchedFields.email}
                  />
                </div>
                <div>
                  <Input
                    id="fullName"
                    label="Full name"
                    placeholder="Your full name"
                    disabled={isLoading}
                    {...register("fullName")}
                    error={errors.fullName?.message}
                    touched={touchedFields.fullName}
                  />
                </div>
                <div>
                  <Input
                    id="password"
                    label="Password"
                    placeholder="Create a password"
                    type="password"
                    disabled={isLoading}
                    {...register("password")}
                    error={errors.password?.message}
                    touched={touchedFields.password}
                  />
                </div>
              </div>

              {formError && <ErrorCard message={formError} />}

              <Label className="text-sm text-muted-foreground">
                By clicking Create account, you agree to our{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://ledgerbeam.com/terms"
                  className="text-primary underline dark:text-primary"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://ledgerbeam.com/privacy"
                  className="text-primary underline dark:text-primary"
                >
                  Privacy Policy
                </a>
              </Label>
              <Button type="submit" disabled={isLoading} className="mt-3 w-full">
                {isLoading ? "Creating account..." : "Create account"}
              </Button>
              <div className="my-5 flex items-center">
                <div className="grow border-t dark:border-zinc-700" />
                <span className="mx-2 text-xs text-muted-foreground">OR</span>
                <div className="grow border-t dark:border-zinc-700" />
              </div>
              <div className="grid gap-4">
                <Button
                  variant="outline"
                  type="button"
                  disabled={isLoading || googleLoading}
                  onClick={handleGoogleAuth}
                  className="flex w-full items-center justify-center"
                >
                  {googleLoading ? (
                    <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <img
                      src="https://logos.ledgerbeam.com/google.com"
                      alt="Google"
                      width={20}
                      height={20}
                      className="mr-2"
                    />
                  )}
                  Sign up with Google
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  disabled={isLoading || githubLoading}
                  onClick={handleGitHubAuth}
                  className="flex w-full items-center justify-center"
                >
                  {githubLoading ? (
                    <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <img
                      src="https://logos.ledgerbeam.com/github.com"
                      alt="GitHub"
                      width={20}
                      height={20}
                      className="mr-2"
                    />
                  )}
                  Sign up with GitHub
                </Button>
              </div>
            </form>
          </div>
          <div className="-mt-4 w-full max-w-lg rounded-b-xl border-t bg-gray-50 px-8 py-5 text-center text-sm text-muted-foreground shadow-md shadow-gray-200 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-700">
            Already have an account?{" "}
            <Link to={ROUTES.AUTH.LOGIN} className="font-medium text-primary hover:underline dark:text-primary">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
